import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { Base, Geometry, Subtraction } from '@react-three/csg'
import WallShaderMaterial from '../../materials/WallShaderMaterial'
import { extend } from '@react-three/fiber'
import { cloneAndScaleUV, setupTextureRepeat } from '../../utils/geometryUltis'
import applyNormalDirectionFilter from '../../utils/normalDirectionFilter'

extend({ WallShaderMaterial })

function ensureBoundingBox(geo) {
  if (geo && !geo.boundingBox) geo.computeBoundingBox()
  return geo
}

function getCenterFromGeo(geo) {
  ensureBoundingBox(geo)
  const c = new THREE.Vector3()
  geo.boundingBox?.getCenter(c)
  return c
}

function unionBoxes(geos) {
  const box = new THREE.Box3()
  geos.forEach((g) => {
    ensureBoundingBox(g)
    if (g?.boundingBox) box.union(g.boundingBox)
  })
  return box
}

/**
 * ✅ OBrys dachu bez wewnętrznych krawędzi:
 * - convex hull w XZ z wierzchołków dachu (WORLD),
 * - Y dobieramy raycastem w dach (żeby punkty leżały na połaci).
 * Zwraca segmenty w WORLD (po okapie, bo bierze wierzchołki dachu).
 */
function getRoofPerimeterHullSegmentsWorld(roofMesh, epsXZ = 1e-4) {
  if (!roofMesh?.geometry?.attributes?.position) return []

  const geom = roofMesh.geometry
  const pos = geom.attributes.position

  const uniq = new Map()
  const keyXZ = (x, z) => `${Math.round(x / epsXZ)},${Math.round(z / epsXZ)}`

  const v = new THREE.Vector3()
  for (let i = 0; i < pos.count; i++) {
    v.set(pos.getX(i), pos.getY(i), pos.getZ(i))
    roofMesh.localToWorld(v)

    const k = keyXZ(v.x, v.z)
    if (!uniq.has(k)) uniq.set(k, { x: v.x, z: v.z })
  }

  const pts = Array.from(uniq.values())
  if (pts.length < 3) return []

  // convex hull (monotonic chain) w 2D: (x,z)
  pts.sort((a, b) => (a.x === b.x ? a.z - b.z : a.x - b.x))

  const cross = (o, a, b) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x)

  const lower = []
  for (const p of pts) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
      lower.pop()
    }
    lower.push(p)
  }

  const upper = []
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
      upper.pop()
    }
    upper.push(p)
  }

  const hull = lower.slice(0, -1).concat(upper.slice(0, -1))
  if (hull.length < 3) return []

  // raycast po Y dla każdego punktu hull
  const roofWorldBox = new THREE.Box3().setFromObject(roofMesh)
  const originY = roofWorldBox.max.y + 5

  const raycaster = new THREE.Raycaster()
  const down = new THREE.Vector3(0, -1, 0)

  const pickY = (x, z) => {
    raycaster.set(new THREE.Vector3(x, originY, z), down)
    const hits = raycaster.intersectObject(roofMesh, false)
    if (hits && hits.length) return hits[0].point.y
    return roofWorldBox.max.y
  }

  const points3D = hull.map((p) => new THREE.Vector3(p.x, pickY(p.x, p.z), p.z))

  // segmenty (zamknięta pętla)
  const segments = []
  for (let i = 0; i < points3D.length; i++) {
    const a = points3D[i]
    const b = points3D[(i + 1) % points3D.length]
    if (a.distanceToSquared(b) < 1e-10) continue
    segments.push({ a: a.clone(), b: b.clone() })
  }

  return segments
}

export default function GarazSpadWPrawo({
                                          gatesData,
                                          doorsData,
                                          windowsData,
                                          textures,
                                          onTrimsData,
                                          ...props
                                        }) {
  const { nodes, materials } = useGLTF('/models/garages/spad-w-prawo.glb')

  const scaledRightGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Right_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Right_side.geometry, props.length, props.height, props.width])

  const scaledLeftGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Left_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.height / 100,
      { flipU: true }
    )
    return ensureBoundingBox(g)
  }, [nodes.Left_side.geometry, props.length, props.height, props.width])

  const scaledBackGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Back_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Back_side.geometry, props.length, props.height, props.width])

  const scaledFrontGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Front_side.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.width / 100,
      props.height / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Front_side.geometry, props.length, props.height, props.width])

  const scaledRoofGeometry = useMemo(() => {
    const g = cloneAndScaleUV(
      nodes.Celing.geometry,
      props.length / 100,
      props.height / 100,
      props.width / 100,
      props.length / 100,
      props.width / 100
    )
    return ensureBoundingBox(g)
  }, [nodes.Celing.geometry, props.length, props.height, props.width])

  // ===== centrum garażu + kierunki "na zewnątrz" (object space) =====
  const garageCenter = useMemo(() => {
    const box = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
    const c = new THREE.Vector3()
    box.getCenter(c)
    return c
  }, [scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])

  const dirLeft = useMemo(() => getCenterFromGeo(scaledLeftGeometry).sub(garageCenter).normalize(), [scaledLeftGeometry, garageCenter])
  const dirRight = useMemo(() => getCenterFromGeo(scaledRightGeometry).sub(garageCenter).normalize(), [scaledRightGeometry, garageCenter])
  const dirFront = useMemo(() => getCenterFromGeo(scaledFrontGeometry).sub(garageCenter).normalize(), [scaledFrontGeometry, garageCenter])
  const dirBack = useMemo(() => getCenterFromGeo(scaledBackGeometry).sub(garageCenter).normalize(), [scaledBackGeometry, garageCenter])

  // ===== windows flatten =====
  const [flattenedWindows, setFlattenedWindows] = useState({})

  const flattenWindows = (data) => {
    const result = {}
    if (!data) return result

    Object.entries(data).forEach(([wallKey, windowTypes]) => {
      const wallWindows = []
      Object.entries(windowTypes || {}).forEach(([typeKey, windows]) => {
        if (!Array.isArray(windows)) return
        windows.forEach((w) => wallWindows.push({ ...w, wall: wallKey, type: typeKey }))
      })
      result[wallKey] = wallWindows
    })

    return result
  }

  useEffect(() => {
    setFlattenedWindows(flattenWindows(windowsData))
  }, [windowsData])

  // ===== subtractions =====
  const subtractionGeometriesFrontWall = useMemo(() => {
    const tmp = []

    if (gatesData?.length) {
      gatesData.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
        })
      })
    }

    if (doorsData?.front_wall?.length) {
      doorsData.front_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [gatesData, doorsData?.front_wall])

  const subtractionGeometriesBackWall = useMemo(() => {
    const tmp = []

    if (doorsData?.back_wall?.length) {
      doorsData.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.back_wall?.length) {
      flattenedWindows.back_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.back_wall, flattenedWindows?.back_wall])

  const subtractionGeometriesLeftWall = useMemo(() => {
    const tmp = []

    if (doorsData?.left_wall?.length) {
      doorsData.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.left_wall?.length) {
      flattenedWindows.left_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.left_wall, flattenedWindows?.left_wall])

  const subtractionGeometriesRightWall = useMemo(() => {
    const tmp = []

    if (doorsData?.right_wall?.length) {
      doorsData.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    if (flattenedWindows?.right_wall?.length) {
      flattenedWindows.right_wall.forEach((wallData) => {
        if (!wallData.closedBoundingBox) return
        const { size } = wallData.closedBoundingBox
        tmp.push({
          geometry: new THREE.BoxGeometry(size.x, size.y, size.z),
          position: wallData.positionAdjusted || [0, 0, 0],
          scale: wallData.scale,
        })
      })
    }

    return tmp
  }, [doorsData?.right_wall, flattenedWindows?.right_wall])

  // ===== materials =====
  const applyRepeat = (tex, rx, ry) => {
    if (!tex) return
    setupTextureRepeat(tex, rx, ry)
  }

  const baseTemplate = useMemo(() => {
    return materials?.Material || materials?.['Material'] || new THREE.MeshStandardMaterial()
  }, [materials])

  const cloneBase = () => baseTemplate.clone()
  const EPS = 0.02

  const makeOuterWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_color

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'out', EPS)
    m.needsUpdate = true
    return m
  }

  const makeInnerWallMaterial = (repeatX, repeatY, dir) => {
    const m = cloneBase()
    const wallColor = textures?.walls_inside_color

    if (wallColor) {
      m.map = wallColor.textureDiff || null
      m.metalnessMap = wallColor.textureMetalness || null
      m.normalMap = wallColor.textureNormalness || null

      applyRepeat(m.map, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.metalnessMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)
      applyRepeat(m.normalMap, repeatX / wallColor.multiplierHorizontal, repeatY / wallColor.multiplierVertical)

      m.metalness = wallColor.metalnessValue
      m.roughness = wallColor.roughnessValue
      m.color = new THREE.Color(wallColor.hex)
    } else {
      m.color = new THREE.Color('#f5f5f5')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, dir, 'in', EPS)
    m.needsUpdate = true
    return m
  }

  const leftOuterMat = useMemo(() => makeOuterWallMaterial(props.length / 100, 1, dirLeft), [baseTemplate, textures?.walls_color, props.length, dirLeft])
  const leftInnerMat = useMemo(() => makeInnerWallMaterial(props.length / 100, 1, dirLeft), [baseTemplate, textures?.walls_inside_color, props.length, dirLeft])

  const rightOuterMat = useMemo(() => makeOuterWallMaterial(props.length / 100, 1, dirRight), [baseTemplate, textures?.walls_color, props.length, dirRight])
  const rightInnerMat = useMemo(() => makeInnerWallMaterial(props.length / 100, 1, dirRight), [baseTemplate, textures?.walls_inside_color, props.length, dirRight])

  const frontOuterMat = useMemo(() => makeOuterWallMaterial(1, 1, dirFront), [baseTemplate, textures?.walls_color, dirFront])
  const frontInnerMat = useMemo(() => makeInnerWallMaterial(1, 1, dirFront), [baseTemplate, textures?.walls_inside_color, dirFront])

  const backOuterMat = useMemo(() => makeOuterWallMaterial(1, 1, dirBack), [baseTemplate, textures?.walls_color, dirBack])
  const backInnerMat = useMemo(() => makeInnerWallMaterial(1, 1, dirBack), [baseTemplate, textures?.walls_inside_color, dirBack])

  // ==========================================================
  // ROOF: outer+inner, a filc tylko inner (moisture_protection==2)
  // ==========================================================
  const roofOuterDir = useMemo(() => {
    const nAttr = scaledRoofGeometry?.attributes?.normal
    let sign = 1

    if (nAttr?.array) {
      const arr = nAttr.array
      let sumY = 0
      for (let i = 1; i < arr.length; i += 3) sumY += arr[i]
      sign = sumY >= 0 ? 1 : -1
    }

    const local = new THREE.Vector3(0, sign, 0)

    const r = nodes?.Celing?.rotation
    if (r) local.applyEuler(r)

    return local.normalize()
  }, [
    scaledRoofGeometry,
    nodes?.Celing?.rotation?.x,
    nodes?.Celing?.rotation?.y,
    nodes?.Celing?.rotation?.z
  ])

  const RoofOuterMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null

    m.side = THREE.DoubleSide

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'out', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofInnerMaterial = useMemo(() => {
    const m = cloneBase()
    const RoofColor = textures?.roof_color || null

    m.side = THREE.DoubleSide

    if (RoofColor) {
      m.map = RoofColor.textureDiff || null
      m.metalnessMap = RoofColor.textureMetalness || null
      m.normalMap = RoofColor.textureNormalness || null

      applyRepeat(m.map, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.metalnessMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))
      applyRepeat(m.normalMap, 1 / (RoofColor.multiplierHorizontal || 1), 1 / (RoofColor.multiplierVertical || 1))

      m.metalness = RoofColor.metalnessValue ?? 0
      m.roughness = RoofColor.roughnessValue ?? RoofColor.rougnessValue ?? 1
      m.color = new THREE.Color(RoofColor.hex ?? '#ffffff')
    } else {
      m.color = new THREE.Color('#ffffff')
      m.metalness = 0
      m.roughness = 1
    }

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, roofOuterDir])

  const RoofFilcMaterial = useMemo(() => {
    if (Number(props?.moisture_protection) !== 2) return null

    const RoofColor = textures?.roof_color || null
    const filcTex = RoofColor?.texture_filc || null
    if (!filcTex) return null

    const wallsTex = textures?.walls_color || null

    try {
      filcTex.colorSpace = THREE.SRGBColorSpace
      filcTex.needsUpdate = true
    } catch (e) {}

    const m = cloneBase()
    m.side = THREE.DoubleSide

    m.map = filcTex
    applyRepeat(m.map, 1, 1)

    m.normalMap = wallsTex?.textureNormalness || null
    if (m.normalMap) {
      applyRepeat(
        m.normalMap,
        1 / (wallsTex.multiplierHorizontal || 1),
        1 / (wallsTex.multiplierVertical || 1)
      )
    }

    m.metalnessMap = null
    m.metalness = 0
    m.roughness = 1
    m.color = new THREE.Color('#ffffff')

    applyNormalDirectionFilter(m, roofOuterDir, 'in', EPS)
    m.needsUpdate = true
    return m
  }, [baseTemplate, textures?.roof_color, textures?.walls_color, props?.moisture_protection, roofOuterDir])

  const RoofInsideMaterial = RoofFilcMaterial || RoofInnerMaterial

  // ==========================================================
  // ✅ TOOLING (okap):
  // - roofEdges: obrys dachu (okap) bez wewnętrznych
  // - corners: narożniki ścian (bez okapu) + Y z raycastu w dach
  // ==========================================================
  const groupRef = useRef(null)
  const roofOuterRef = useRef(null)

  const frontWallRef = useRef(null)
  const backWallRef = useRef(null)
  const leftWallRef = useRef(null)
  const rightWallRef = useRef(null)

  useLayoutEffect(() => {
    if (!onTrimsData) return
    if (!groupRef.current || !roofOuterRef.current) return

    groupRef.current.updateWorldMatrix(true, true)
    roofOuterRef.current.updateWorldMatrix(true, true)

    // 1) obrys dachu po okapie (WORLD)
    const roofEdges = getRoofPerimeterHullSegmentsWorld(roofOuterRef.current, 1e-4)

    // 2) box ścian w WORLD (żeby działało nawet jeśli node ma rotacje)
    const wallsBoxWorld = new THREE.Box3()
    wallsBoxWorld.makeEmpty()

    const wallMeshes = [frontWallRef.current, backWallRef.current, leftWallRef.current, rightWallRef.current].filter(Boolean)
    if (wallMeshes.length) {
      for (const m of wallMeshes) {
        m.updateWorldMatrix(true, true)
        wallsBoxWorld.union(new THREE.Box3().setFromObject(m))
      }
    } else {
      // fallback (gdyby refy nie złapały) - mniej dokładny
      const wallsBoxLocal = unionBoxes([scaledLeftGeometry, scaledRightGeometry, scaledFrontGeometry, scaledBackGeometry])
      wallsBoxWorld.copy(wallsBoxLocal).applyMatrix4(groupRef.current.matrixWorld)
    }

    const groundY = wallsBoxWorld.min.y

    // 3) raycast: wysokość dachu nad (x,z)
    const getRoofYAtXZ = (roofMesh, x, z) => {
      const roofWorldBox = new THREE.Box3().setFromObject(roofMesh)
      const originY = roofWorldBox.max.y + 5

      const raycaster = new THREE.Raycaster()
      raycaster.set(new THREE.Vector3(x, originY, z), new THREE.Vector3(0, -1, 0))

      const hits = raycaster.intersectObject(roofMesh, false)
      if (hits && hits.length) return hits[0].point.y

      return roofWorldBox.max.y
    }

    // 4) narożniki ścian (WORLD XZ) + Y z dachu
    const minX = wallsBoxWorld.min.x
    const maxX = wallsBoxWorld.max.x
    const minZ = wallsBoxWorld.min.z
    const maxZ = wallsBoxWorld.max.z

    const makeCornerTop = (x, z) => {
      const yRoof = getRoofYAtXZ(roofOuterRef.current, x, z)
      return new THREE.Vector3(x, yRoof, z)
    }

    const roofCorners = {
      frontLeft: makeCornerTop(minX, minZ),
      frontRight: makeCornerTop(minX, maxZ),
      backLeft: makeCornerTop(maxX, minZ),
      backRight: makeCornerTop(maxX, maxZ),
    }

    const cornerSegments = Object.entries(roofCorners).map(([key, top]) => ({
      key,
      a: new THREE.Vector3(top.x, groundY, top.z),
      b: top.clone(),
    }))

    onTrimsData({
      roofEdges,
      corners: cornerSegments,
      roofCorners,
      groundY,
    })
  }, [
    onTrimsData,
    props.length,
    props.width,
    props.height,
    scaledLeftGeometry,
    scaledRightGeometry,
    scaledFrontGeometry,
    scaledBackGeometry,
    scaledRoofGeometry,
    gatesData,
    doorsData,
    windowsData,
  ])

  return (
    <group ref={groupRef} {...props} dispose={null} castShadow receiveShadow>
      {/* ROOF outer (ref do toolingu) */}
      <mesh
        ref={roofOuterRef}
        geometry={scaledRoofGeometry}
        material={RoofOuterMaterial}
        rotation={nodes.Celing.rotation}
      />

      {/* ROOF inner: zawsze */}
      <mesh
        geometry={scaledRoofGeometry}
        material={RoofInsideMaterial}
        rotation={nodes.Celing.rotation}
      />

      {/* FRONT OUTER */}
      <mesh ref={frontWallRef} name="Front_side_outer" material={frontOuterMat} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* FRONT INNER */}
      <mesh name="Front_side_inner" material={frontInnerMat} rotation={nodes.Front_side.rotation}>
        <Geometry>
          <Base geometry={scaledFrontGeometry} />
          {subtractionGeometriesFrontWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT OUTER */}
      <mesh ref={leftWallRef} name="Left_side_outer" material={leftOuterMat} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* LEFT INNER */}
      <mesh name="Left_side_inner" material={leftInnerMat} rotation={nodes.Left_side.rotation}>
        <Geometry>
          <Base geometry={scaledLeftGeometry} />
          {subtractionGeometriesLeftWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT OUTER */}
      <mesh ref={rightWallRef} name="Right_side_outer" material={rightOuterMat} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* RIGHT INNER */}
      <mesh name="Right_side_inner" material={rightInnerMat} rotation={nodes.Right_side.rotation}>
        <Geometry>
          <Base geometry={scaledRightGeometry} />
          {subtractionGeometriesRightWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK OUTER */}
      <mesh ref={backWallRef} name="Back_side_outer" material={backOuterMat} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>

      {/* BACK INNER */}
      <mesh name="Back_side_inner" material={backInnerMat} rotation={nodes.Back_side.rotation}>
        <Geometry>
          <Base geometry={scaledBackGeometry} />
          {subtractionGeometriesBackWall.map(({ geometry, position, scale }, index) => (
            <Subtraction key={index} geometry={geometry} position={position} scale={scale} />
          ))}
        </Geometry>
      </mesh>
    </group>
  )
}
